import React, { useEffect, useRef, useState } from "react";
import "./landingPage.css";
import { Tag } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getFirstName } from "../../helper/userDisplay";
import { statusMeta } from "../../helper/statusMeta";
import useDashboardData from "./useDashboardData";
import EmailReportsSummary from "../../components/emailReportsSummary/EmailReportsSummary";

// The unlisted App Store listing — the link admins share so teammates can
// install the app on their phone. Also embedded in every invite email.
const APP_STORE_URL =
  "https://apps.apple.com/us/app/2nd-home-inspections/id6781021908";

/* Count-up that respects reduced-motion and only animates once mounted. */
const CountUp = ({ value = 0, suffix = "", duration = 1100 }) => {
  const [display, setDisplay] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (reduce || !value) {
      setDisplay(value);
      return;
    }
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [value, duration]);

  return (
    <span className="sh-numeric">
      {display.toLocaleString()}
      {suffix}
    </span>
  );
};

const StatCard =({ eyebrow, value, suffix, caption, delay, loading }) => (
  <div className="sh-stat sh-rise" style={{ animationDelay: `${delay}ms` }}>
    <span className="sh-stat-eyebrow">{eyebrow}</span>
    <div className="sh-stat-value">
      {loading ? (
        <span className="sh-skeleton sh-skeleton-num" />
      ) : (
        <CountUp value={value} suffix={suffix} />
      )}
    </div>
    <span className="sh-stat-caption">{caption}</span>
  </div>
);

// FR-13: a lightweight CSS bar chart for the weekly trend (no chart dependency).
const TrendChart = ({ title, data, field, tone }) => {
  const max = Math.max(1, ...data.map((d) => d[field]));
  return (
    <div className="sh-trend-block">
      <div className="sh-trend-title">{title}</div>
      <div className="sh-bars">
        {data.map((d, i) => (
          <div className="sh-bar-col" key={i}>
            <span className="sh-bar-val">{d[field] || ""}</span>
            <div
              className={`sh-bar sh-bar-${tone}`}
              style={{ height: `${Math.round((d[field] / max) * 100)}%` }}
              title={`${d.label}: ${d[field]}`}
            />
            <span className="sh-bar-label">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.userDataSlice?.userData);
  const firstName = getFirstName(user);

  // Copy the App Store install link to the clipboard, with a brief "Copied"
  // confirmation. Falls back to execCommand for older/non-secure contexts.
  const [linkCopied, setLinkCopied] = useState(false);
  const copyAppLink = async () => {
    try {
      await navigator.clipboard.writeText(APP_STORE_URL);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = APP_STORE_URL;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch (e) {
        /* clipboard unavailable — the link stays visible for manual copy */
      }
      document.body.removeChild(ta);
    }
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const {
    totalUsers,
    totalProperties,
    openInspections,
    completionRate,
    statusBreakdown,
    recentInspections,
    inspectorPerformance,
    flaggedIssues,
    weeklyTrend,
    openQuestions,
    loading,
    error,
  } = useDashboardData();

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  const missed = statusBreakdown?.missed || 0;
  const total = statusBreakdown
    ? statusBreakdown.pending +
      statusBreakdown["in progress"] +
      statusBreakdown.completed +
      missed
    : 0;
  const pct = (n) => (total ? (n / total) * 100 : 0);

  const quickActions = [
    { label: "Add a user", hint: "Create an account", path: "/users" },
    {
      label: "New inspection",
      hint: "Assign a property",
      path: "/properties",
      accent: true,
    },
    { label: "Manage content", hint: "FAQs & about", path: "/about-us" },
    { label: "Notifications", hint: "Alerts & updates", path: "/notifications" },
  ];

  return (
    <div className="sh-dash sh-atmosphere">
      <header className="sh-dash-head sh-rise">
        <div>
          <p className="sh-dash-date">{today}</p>
          <h1 className="sh-dash-greeting">
            {greeting}, <span className="sh-dash-accent">{firstName}</span>
          </h1>
          <p className="sh-dash-sub">
            Here&rsquo;s how 2nd Home is doing across your properties today.
          </p>
        </div>
        <button className="sh-cta" onClick={() => navigate("/properties")}>
          New inspection <span aria-hidden>→</span>
        </button>
      </header>

      <section className="sh-applink sh-rise" style={{ animationDelay: "40ms" }}>
        <div className="sh-applink-main">
          <span className="sh-applink-eyebrow">App install link</span>
          <p className="sh-applink-desc">
            Share this so teammates can install 2nd Home on their iPhone or iPad.
            It&rsquo;s also included in every invite email.
          </p>
          <code className="sh-applink-url">{APP_STORE_URL}</code>
        </div>
        <div className="sh-applink-actions">
          <button
            className={`sh-applink-copy${linkCopied ? " is-copied" : ""}`}
            onClick={copyAppLink}
          >
            {linkCopied ? "Copied ✓" : "Copy link"}
          </button>
          <a
            className="sh-applink-open"
            href={APP_STORE_URL}
            target="_blank"
            rel="noreferrer"
          >
            Open ↗
          </a>
        </div>
      </section>

      {error && (
        <div className="sh-notice sh-rise">
          Couldn&rsquo;t reach the live data right now — showing what we have.
          Check your connection and refresh.
        </div>
      )}

      <section className="sh-stat-grid">
        <StatCard
          eyebrow="Total users"
          value={totalUsers}
          caption="Registered accounts"
          delay={60}
          loading={loading}
        />
        <StatCard
          eyebrow="Properties"
          value={totalProperties}
          caption="Properties on file"
          delay={120}
          loading={loading}
        />
        <StatCard
          eyebrow="Open"
          value={openInspections}
          caption="Pending or in progress"
          delay={180}
          loading={loading}
        />
        <StatCard
          eyebrow="Completion"
          value={completionRate}
          suffix="%"
          caption="Of all inspections"
          delay={240}
          loading={loading}
        />
      </section>

      <section className="sh-panels">
        {/* Main column — the two detail lists, stacked so the column runs the
            full height alongside the widget rail (no ragged empty space). */}
        <div className="sh-rail">
          {/* Recent inspections */}
          <div className="sh-panel sh-rise" style={{ animationDelay: "300ms" }}>
            <div className="sh-panel-head">
              <h2 className="sh-panel-title">Recent inspections</h2>
              <button
                className="sh-link"
                onClick={() => navigate("/properties")}
              >
                View all
              </button>
            </div>

            {loading ? (
              <div className="sh-rows">
                {[0, 1, 2, 3].map((i) => (
                  <div className="sh-row" key={i}>
                    <span className="sh-skeleton sh-skeleton-line" />
                  </div>
                ))}
              </div>
            ) : recentInspections.length === 0 ? (
              <div className="sh-empty">
                No inspections yet. Assign one to get started.
              </div>
            ) : (
              <div className="sh-rows">
                {recentInspections.map((item) => {
                  const meta = statusMeta(item.status);
                  return (
                    <div className="sh-row" key={item.id}>
                      <div className="sh-row-main">
                        <span className="sh-row-name">{item.name}</span>
                        <span className="sh-row-meta">
                          {item.assignee} · {item.date}
                        </span>
                      </div>
                      <Tag color={meta.color}>{meta.label}</Tag>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Inspector performance */}
          <div className="sh-panel sh-rise" style={{ animationDelay: "480ms" }}>
            <div className="sh-panel-head">
              <h2 className="sh-panel-title">Inspector performance</h2>
            </div>

            {loading ? (
              <div className="sh-rows">
                {[0, 1, 2, 3].map((i) => (
                  <div className="sh-row" key={i}>
                    <span className="sh-skeleton sh-skeleton-line" />
                  </div>
                ))}
              </div>
            ) : !inspectorPerformance || inspectorPerformance.length === 0 ? (
              <div className="sh-empty">
                No inspector activity yet. Assign an inspection to get started.
              </div>
            ) : (
              <div className="sh-rows">
                {inspectorPerformance.map((p) => (
                  <div className="sh-row" key={p.name}>
                    <div className="sh-row-main">
                      <span className="sh-row-name">{p.name}</span>
                      <span className="sh-row-meta">
                        {p.completed} of {p.total} completed · {p.pending} open
                      </span>
                    </div>
                    <Tag color={p.completionRate >= 50 ? "success" : "warning"}>
                      {p.completionRate}%
                    </Tag>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Side column — compact widgets stacked to fill the height */}
        <div className="sh-rail">
          {/* Report emails — first in the rail: it carries the held-for-review
              count, the most action-required state on this page. */}
          <EmailReportsSummary />

          {/* Questions to answer — inspectors waiting on a reply */}
          <div className="sh-panel sh-rise" style={{ animationDelay: "330ms" }}>
            <div className="sh-panel-head">
              <h2 className="sh-panel-title">Questions to answer</h2>
              <button
                className="sh-link"
                onClick={() => navigate("/tasks?questions=1")}
              >
                Review
              </button>
            </div>
            {loading ? (
              <div className="sh-rows">
                <div className="sh-row">
                  <span className="sh-skeleton sh-skeleton-line" />
                </div>
              </div>
            ) : !openQuestions ? (
              <div className="sh-empty">
                No open questions. You&rsquo;re all caught up.
              </div>
            ) : (
              <div
                className="sh-row"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/tasks?questions=1")}
              >
                <div className="sh-row-main">
                  <span className="sh-row-name">
                    {openQuestions} {openQuestions === 1 ? "task" : "tasks"}{" "}
                    waiting on a reply
                  </span>
                  <span className="sh-row-meta">
                    A teammate asked a question — answer it in Tasks
                  </span>
                </div>
                <Tag color="error">{openQuestions}</Tag>
              </div>
            )}
          </div>

          {/* By status */}
          <div className="sh-panel sh-rise" style={{ animationDelay: "360ms" }}>
            <div className="sh-panel-head">
              <h2 className="sh-panel-title">By status</h2>
            </div>
            <div className="sh-bar" aria-hidden>
              <span
                className="sh-bar-seg seg-completed"
                style={{ width: `${pct(statusBreakdown.completed)}%` }}
              />
              <span
                className="sh-bar-seg seg-progress"
                style={{ width: `${pct(statusBreakdown["in progress"])}%` }}
              />
              <span
                className="sh-bar-seg seg-pending"
                style={{ width: `${pct(statusBreakdown.pending)}%` }}
              />
              {missed > 0 && (
                <span
                  className="sh-bar-seg seg-missed"
                  style={{ width: `${pct(missed)}%` }}
                />
              )}
            </div>
            <ul className="sh-legend">
              <li>
                <span className="dot seg-completed" /> Completed
                <b className="sh-numeric">{statusBreakdown.completed}</b>
              </li>
              <li>
                <span className="dot seg-progress" /> In progress
                <b className="sh-numeric">{statusBreakdown["in progress"]}</b>
              </li>
              <li>
                <span className="dot seg-pending" /> Pending
                <b className="sh-numeric">{statusBreakdown.pending}</b>
              </li>
              {/* Missed needs an admin's eye (reschedule or write off), so it
                  only appears when non-zero and links to the scheduler. */}
              {missed > 0 && (
                <li
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate("/inspections?filter=Missed")}
                >
                  <span className="dot seg-missed" /> Missed
                  <b className="sh-numeric">{missed}</b>
                </li>
              )}
            </ul>
          </div>

          {/* Quick actions */}
          <div className="sh-panel sh-rise" style={{ animationDelay: "420ms" }}>
            <div className="sh-panel-head">
              <h2 className="sh-panel-title">Quick actions</h2>
            </div>
            <div className="sh-actions">
              {quickActions.map((a) => (
                <button
                  key={a.label}
                  className={`sh-action${a.accent ? " is-accent" : ""}`}
                  onClick={() => navigate(a.path)}
                >
                  <span className="sh-action-label">{a.label}</span>
                  <span className="sh-action-hint">{a.hint}</span>
                  <span className="sh-action-arrow" aria-hidden>
                    →
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Flagged issues */}
          <div className="sh-panel sh-rise" style={{ animationDelay: "540ms" }}>
            <div className="sh-panel-head">
              <h2 className="sh-panel-title">Flagged issues</h2>
            </div>

            {loading ? (
              <div className="sh-rows">
                {[0, 1, 2].map((i) => (
                  <div className="sh-row" key={i}>
                    <span className="sh-skeleton sh-skeleton-line" />
                  </div>
                ))}
              </div>
            ) : !flaggedIssues || flaggedIssues.totalFlagged === 0 ? (
              <div className="sh-empty">
                No flagged issues yet. Completed inspections with problems will
                show here.
              </div>
            ) : (
              <>
                <ul className="sh-legend">
                  <li>
                    Total flagged
                    <b className="sh-numeric">{flaggedIssues.totalFlagged}</b>
                  </li>
                  <li>
                    Inspections affected
                    <b className="sh-numeric">
                      {flaggedIssues.inspectionsWithIssues}
                    </b>
                  </li>
                </ul>
                {flaggedIssues.byProperty.length > 0 && (
                  <div className="sh-rows">
                    {flaggedIssues.byProperty.slice(0, 5).map((row) => (
                      <div className="sh-row" key={row.propertyName}>
                        <div className="sh-row-main">
                          <span className="sh-row-name">
                            {row.propertyName}
                          </span>
                          <span className="sh-row-meta">
                            {row.count} flagged{" "}
                            {row.count === 1 ? "issue" : "issues"}
                          </span>
                        </div>
                        <Tag color="error">{row.count}</Tag>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* FR-13: trends over time */}
      <section className="sh-panels" style={{ gridTemplateColumns: "1fr" }}>
        <div className="sh-panel sh-rise" style={{ animationDelay: "600ms" }}>
          <div className="sh-panel-head">
            <h2 className="sh-panel-title">Trends — last 8 weeks</h2>
          </div>
          {loading ? (
            <div className="sh-rows">
              {[0, 1, 2].map((i) => (
                <div className="sh-row" key={i}>
                  <span className="sh-skeleton sh-skeleton-line" />
                </div>
              ))}
            </div>
          ) : !weeklyTrend ||
            weeklyTrend.every((w) => w.completed === 0 && w.flagged === 0) ? (
            <div className="sh-empty">
              No completed inspections in the last 8 weeks yet. Completed
              inspections will chart here.
            </div>
          ) : (
            <div className="sh-trend">
              <TrendChart
                title="Completed inspections"
                data={weeklyTrend}
                field="completed"
                tone="ok"
              />
              <TrendChart
                title="Flagged issues"
                data={weeklyTrend}
                field="flagged"
                tone="bad"
              />
            </div>
          )}
        </div>
      </section>

      <footer className="sh-dash-foot">
        © {new Date().getFullYear()} 2nd Home · Property Inspection
      </footer>
    </div>
  );
};

export default LandingPage;
